import loadable from '@loadable/component';
import { RelayPullToRefresh } from 'components/PullToRefresh';
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { CalendarViewProvider } from 'screens/Calendar/providers';
import LoadingScreen from 'screens/Loading';
import { graphql, RelaySafeProvider, useSafeRelay } from 'utils/graphClient';
import {
  asterisk,
  ATTACHMENTS_PATH_PREFIX,
  EVENTS_PATH_PREFIX,
  FAMILY_TREE_PATH_PREFIX,
  MEMBERS_PATH_PREFIX,
  pathParam,
  SPACE_DELETED_PATH,
  SPACE_PARAM,
} from 'utils/routes';
import useSafeParam from 'utils/routes/useSafeParam';
import { CenteredSpinner } from 'components/CenteredSpinner';
import { GenericErrorFallback } from 'components/GenericErrorBoundary';
import NotFound from '../NotFound';
import { SpaceRouter_spaceQuery } from './__generated__/SpaceRouter_spaceQuery.graphql';
import { SpaceLayout } from './components/SpaceLayout';
import SpaceDeleted from './screens/Delete';
import { useFeatureFlag } from 'utils/sessionProvider';

const loadingPageFallback = {
  fallback: <LoadingScreen />,
};
const Start = loadable(() => import('../Start'), loadingPageFallback);
Start.preload();

const SpaceActivity = loadable(
  () => import('./screens/Activity'),
  loadingPageFallback,
);

const Events = loadable(() => import('../Events'), loadingPageFallback);

const Members = loadable(
  () => import('./screens/Members'),
  loadingPageFallback,
);

const SpaceAttachments = loadable(
  () => import('./screens/Attachments'),
  loadingPageFallback,
);

const SpaceFiles = loadable(
  () => import('screens/Space/Files'),
  loadingPageFallback,
);

const FamilyTree = loadable(
  () => import('./screens/FamilyTree'),
  loadingPageFallback,
);

const spaceQuery = graphql`
  query SpaceRouter_spaceQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on Space {
        ...SpaceLayout_space
        id
      }
    }
  }
`;

export default () => {
  const isFeatureFilesUXImprovementEnabled = useFeatureFlag(
    'FEATURE_FILES_UX_IMPROVEMENT',
  );

  return (
    <Routes>
      <Route path={SPACE_DELETED_PATH} element={<SpaceDeleted />} />

      <Route index element={<Start />} />

      <Route path={pathParam(SPACE_PARAM)} element={<SpaceLayoutLoader />}>
        <Route index element={<SpaceActivity />} />
        <Route path={asterisk(EVENTS_PATH_PREFIX)} element={<Events />} />
        <Route
          path={ATTACHMENTS_PATH_PREFIX}
          element={
            isFeatureFilesUXImprovementEnabled ? (
              <SpaceFiles />
            ) : (
              <SpaceAttachments />
            )
          }
        />
        <Route path={asterisk(MEMBERS_PATH_PREFIX)} element={<Members />} />
        <Route
          path={asterisk(FAMILY_TREE_PATH_PREFIX)}
          element={<FamilyTree />}
        />
      </Route>
    </Routes>
  );
};

const UnsafeSpaceLayoutLoader = () => {
  const spaceId = useSafeParam(SPACE_PARAM);
  const { useLazyLoadQueryWithRef } = useSafeRelay();
  const [data, queryRef] = useLazyLoadQueryWithRef<SpaceRouter_spaceQuery>(
    spaceQuery,
    {
      id: spaceId,
    },
  );

  if (data.node?.__typename !== 'Space') {
    return <NotFound />;
  }

  return (
    <RelayPullToRefresh queryRef={queryRef}>
      <SpaceLayout space={data.node} />
    </RelayPullToRefresh>
  );
};

const SpaceLayoutLoader = () => {
  return (
    <RelaySafeProvider
      ErrorComponent={(props) => <GenericErrorFallback {...props} />}
      LoadingComponent={() => <CenteredSpinner />}
    >
      <CalendarViewProvider>
        <UnsafeSpaceLayoutLoader />
      </CalendarViewProvider>
    </RelaySafeProvider>
  );
};
